import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Autosuggest from 'react-autosuggest'
import AutosuggestHighlightMatch from 'autosuggest-highlight/match'
import AutosuggestHighlightParse from 'autosuggest-highlight/parse'

import useTypeahead, { spinnerInput } from '@/hooks/useTypeahead'

const CreditAutocomplete = ({ onSelect, placeholder }) => {
  const { get, cancel, loading } = useTypeahead()
  const [credits, setCredits] = useState([])
  const [searchText, setSearchText] = useState('')
  const suggestionSelected = useRef(null)

  const onKeyDown = (event, data) => {
    const { keyCode } = event
    console.log(data)
    if (keyCode === 13 || keyCode === 188 || keyCode === 9) {
      if (searchText.length > 0 && !suggestionSelected.current) {
        onSelect(searchText)
        setSearchText('')
      }
      if (searchText.length !== 0) {
        event.preventDefault()
      }
    }
  }

  useEffect(() => {
    if (searchText === ',') { setSearchText('') } // hack to stop commas from being by themselves
  }, [searchText])

  const fetchData = (newValue) => {
    // no need to hit the server repeatedly when the query is blank
    if (newValue === '') {
      cancel()
      setCredits([])
      return
    }
    get('/api/v3/credits/autosuggest', { contains: newValue }, (err, jsonData) => {
      if (err) { /* hook */ return }
      setCredits(jsonData.credits)
    })
  }

  const handleAutocomplete = (event, { newValue, method }) => {
    if (method === 'up' || method === 'down') return

    setSearchText(newValue)

    if (method === 'enter' || method === 'click') {
      console.log('enter', newValue)
      onSelect(newValue)
      setSearchText('')

      suggestionSelected.current = false
      return
    }

    fetchData(newValue)
  }

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  const getSuggestionValue = suggestion => suggestion ? suggestion.autosuggest : ''
  const onSuggestionSelected = (event, { method }) => {
    suggestionSelected.current = true
  }

  const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const matches = AutosuggestHighlightMatch(suggestion.name, query)
    const parts = AutosuggestHighlightParse(suggestion.name, matches)
    return (
      <div className={`${isHighlighted ? 'bg-cccpurple bg-opacity-20 text-gray-900' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200'} w-full h-full p-2 cursor-pointer capitalize`}>
        {parts.map((part, index) => {
          const className = part.highlight ? 'text-cccblue font-semibold' : null

          return (
            <span className={className} key={index}>
              {part.text}
            </span>
          )
        })}
      </div>
    )
  }

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  const onSuggestionsFetchRequested = ({ value }) => { /* setFranchises(value) */ }
  const onSuggestionsClearRequested = () => { setCredits([]) }
  const shouldRenderSuggestions = (value, reason) => {
    return value.trim().length >= 0
  }

  const onFocus = (event) => {
    // do nothing.
  }

  const inputProps = {
    placeholder: placeholder,
    value: searchText, // must be a string
    type: 'search',
    onKeyDown: onKeyDown,
    onFocus: onFocus,
    autoFocus: true,
    onChange: handleAutocomplete
  }

  return <Autosuggest
    className=''
    suggestions={credits}
    highlightFirstSuggestion={false}
    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
    onSuggestionsClearRequested={onSuggestionsClearRequested}
    shouldRenderSuggestions={shouldRenderSuggestions}
    onSuggestionSelected={onSuggestionSelected}
    getSuggestionValue={getSuggestionValue}
    renderSuggestion={renderSuggestion}
    renderInputComponent={spinnerInput(loading)}
    inputProps={inputProps}
  />
}

export default CreditAutocomplete

CreditAutocomplete.propTypes = {
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired
}
